import { createContext, useContext, useEffect, useState, type ReactNode } from 'react';
import { descargarBundle, guardarBundle, leerBundle, type BundleOperativo } from './bundle';
import { esErrorDeRed, mensajeDeError, sinConexionAhora } from '@/core/red/conectividad';

/**
 * Mantener la copia operativa al día, y ofrecerla cuando no hay servidor.
 *
 * La regla es de una línea: **con señal manda el servidor, sin señal manda la
 * copia**. Nunca al revés, y nunca una mezcla —mostrar la mitad de una pantalla
 * con datos de hoy y la otra mitad con los del martes es peor que mostrar los
 * del martes y decirlo.
 *
 * El refresco es en segundo plano: la pantalla no espera por él. Al abrir la
 * aplicación se lee primero lo guardado —está listo al instante— y si hay
 * servidor se pide de nuevo y se reemplaza. Quien abre en el campo ve sus
 * clientes sin esperar a que un pedido falle.
 */
interface EstadoBundle {
  bundle: BundleOperativo | null;
  /** Cuándo se trajo, para poder decir de cuándo son los datos que se ven. */
  sincronizadoEn: number | null;
  /** Se está trayendo del servidor ahora mismo. */
  refrescando: boolean;
  /**
   * Por qué no se pudo traer, cuando el motivo NO es falta de señal.
   *
   * Existe por un caso que pasó de verdad: la función que devuelve la copia
   * todavía no estaba aplicada en el servidor, así que cada intento fallaba y
   * la pantalla seguía invitando a «conectarse una vez para prepararlo» —que
   * es justo lo que la persona ya había hecho—. Un consejo que no puede
   * funcionar es peor que un error: manda a repetir algo que no va a andar.
   *
   * La falta de señal NO entra acá: eso ya se dice en otro lado y no es un
   * problema que haya que reportar.
   */
  problema: string | null;
  /** Volver a pedirlo. Lo llama la vuelta de la señal. */
  refrescar: () => Promise<void>;
}

const BundleContext = createContext<EstadoBundle | null>(null);

export function BundleProvider({
  organizationId,
  children,
}: {
  organizationId: string | null;
  children: ReactNode;
}) {
  const [bundle, setBundle] = useState<BundleOperativo | null>(null);
  const [refrescando, setRefrescando] = useState(false);
  const [problema, setProblema] = useState<string | null>(null);

  const refrescar = async () => {
    if (!organizationId) return;
    setRefrescando(true);
    try {
      const nuevo = await descargarBundle(organizationId);
      await guardarBundle(nuevo);
      setBundle(nuevo);
      setProblema(null);
    } catch (e) {
      // Sin servidor no se pierde nada: se sigue con la copia que ya estaba.
      // Un refresco fallido NO puede vaciar el bundle, o quedarse sin señal
      // borraría justo lo que hace falta para trabajar sin señal.
      //
      // Pero si el servidor CONTESTÓ que no, eso sí hay que decirlo: es un
      // problema que no se arregla volviendo a conectarse.
      setProblema(esErrorDeRed(e) ? null : mensajeDeError(e));
    } finally {
      setRefrescando(false);
    }
  };

  // Se DERIVA en vez de corregirse desde un efecto: sin organización no hay
  // copia que mostrar, y ponerlo en `null` con `setState` dentro del efecto
  // encadena renders —React 19 lo marca, y con razón—.
  const deLaOrganizacion = bundle?.organizationId === organizationId ? bundle : null;

  useEffect(() => {
    if (!organizationId) return;
    let cancelado = false;

    void (async () => {
      // Primero lo guardado: aparece al instante y sirve aunque no haya red.
      const local = await leerBundle(organizationId);
      if (!cancelado && local) setBundle(local);

      // Y después, si parece haber servidor, se pide de nuevo.
      if (!sinConexionAhora()) {
        const nuevo = await descargarBundle(organizationId).catch((e: unknown) => {
          if (!cancelado) setProblema(esErrorDeRed(e) ? null : mensajeDeError(e));
          return null;
        });
        if (cancelado || !nuevo) return;
        await guardarBundle(nuevo);
        setBundle(nuevo);
        setProblema(null);
      }
    })();

    return () => {
      cancelado = true;
    };
  }, [organizationId]);

  // Cuando vuelve la señal, la copia pasa a ser vieja: se renueva.
  useEffect(() => {
    const alVolver = () => void refrescar();
    window.addEventListener('online', alVolver);
    return () => window.removeEventListener('online', alVolver);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationId]);

  return (
    <BundleContext.Provider
      value={{
        bundle: deLaOrganizacion,
        sincronizadoEn: deLaOrganizacion?.sincronizadoEn ?? null,
        refrescando,
        problema,
        refrescar,
      }}
    >
      {children}
    </BundleContext.Provider>
  );
}

/**
 * La copia operativa.
 *
 * Devuelve `bundle: null` cuando todavía no se descargó ninguna. Eso NO es lo
 * mismo que «no hay clientes», y las pantallas tienen que distinguirlo: decir
 * «0 clientes» cuando en realidad no se pudo consultar es mentir con un número.
 */
export function useBundle(): EstadoBundle {
  const ctx = useContext(BundleContext);
  if (!ctx) throw new Error('useBundle debe usarse dentro de BundleProvider');
  return ctx;
}

/** Como `useBundle`, pero sin exigir el proveedor. Para piezas sueltas. */
export function useBundleOpcional(): EstadoBundle | null {
  return useContext(BundleContext);
}
